import * as React from 'react'
import { Dialog, VisuallyHidden } from '@buffer-mono/popcorn'
import { useSplitEnabled } from '@buffer-mono/features'

import styles from './ModalContent.module.css'

import { MODALS } from '../../common/types'

import Confirmation from './modals/Confirmation/Confirmation'
import ChannelConnectionPrompt from './modals/ChannelConnectionPrompt'
import { PlanSelectorDialog } from '../PlanSelector/PlanSelectorDialog'
import { StartTrialModal } from './modals/StartTrial'
import { FeatureTrialModal } from './modals/FeatureTrial'
import StickyModal from './modals/StickyModal'
import TrialExpired from './modals/TrialExpired'
import ConnectChannelUpgrade from './modals/ConnectChannelUpgrade'
import ChecklistComplete from './modals/ChecklistComplete'
import ChannelRefreshSuccessModal from './modals/ChannelRefreshSuccess'
import { FeaturePaywall } from './modals/FeaturePaywall'
import DownloadAppModal from './modals/DownloadApp/DownloadAppModal'
import { InstagramFacebookReAuth } from './modals/InstagramFacebookReAuth'
import InstagramSelectAccountType from './modals/InstagramSelectAccountType/InstagramSelectAccountType'
import InstagramSwitchToProfessionalAccountWarning from './modals/InstagramSwitchToProfessionalAccountWarning/InstagramSwitchToProfessionalAccountWarning'
import CancelFlowModal from './modals/CancelFlow'
import ConfirmCancellationModal from './modals/ConfirmCancellation'
import TrialSignupWelcomeModal from './modals/TrialSignupWelcomeModal'
import RemindersWizard from './modals/RemindersWizard/components/RemindersWizard/RemindersWizard'
import { TeamMemberDowngradeModal } from './modals/TeamMemberDowngradeModal'
import PostingPlanConfiguration from './modals/PostingPlanConfiguration'
import { useModalManager } from '../ModalManager/hooks/useModalManager'
import { useUser } from '../../common/context/User'
import {
  canManageBilling,
  isOneBufferOrganization,
  isFreeUser,
} from '../../common/utils/user'
import {
  cleanupUrlParams,
  getShouldShowChannelConnectionModal,
  hasSeenChecklistCompleteModal,
  shouldShowChannelConnectionOnboardingModal,
  shouldShowTrialSignupWelcomeModal,
} from './utils'
import { getBillingPageUrl, redirectToUrl } from '../../common/utils/urls'
import { isOnboardingPage } from '../../exports/Navigator/utils/getActiveProductFromUrl'
import { isEligibleForAnnualUpgrade } from '../../exports/Navigator/utils/shouldShowAnnualUpgradeBanner'
import { FacebookSelectAccountTypeDialog } from './modals/FacebookSelectAccountType/FacebookSelectAccountType'
import PostingGoalConfiguration, {
  ChannelConnectionSuccess,
} from './modals/PostingGoalConfiguration'
import ChannelGroupsPrompt from './modals/ChannelGroupsPrompt/ChannelGroupsPrompt'
import SubscriptionUpdateModal from './modals/SubscriptionUpdate/SubscriptionUpdateModal'
import AnalyzeUpgradePath from './modals/AnalyzeUpgradePath'
import { SwitchFromIGLoginToFBLoginDialog } from './modals/SwitchIGLoginToFBLogin/SwitchFromIGLoginToFBLoginDialog'
import { AnnualUpgradeModal } from './modals/AnnualUpgrade'
import { LinkedInCapabilities } from './modals/LinkedInCapabilities'

import type { JSX } from 'react'

const ModalContent = (): JSX.Element | null => {
  const { modalKey, modalData, dismissModal } = useModalManager()
  const user = useUser()
  const { isEnabled: useNewChannelSuccessModal } = useSplitEnabled(
    'geid-set-my-posting-schedule',
  )

  if (!modalKey || !user.id) {
    return null
  }

  const userCanManageBilling = canManageBilling(user)
  const isOneBufferOrg = isOneBufferOrganization(user)

  const shouldShowChannelConnectionOnboarding =
    shouldShowChannelConnectionOnboardingModal(
      user,
      modalData?.channelIds,
      modalData?.channelName,
    )

  const shouldShowConnectChannelUpgradeModal =
    getShouldShowChannelConnectionModal({
      user,
      modalData,
    })

  const shouldShowPostingGoalConfiguration =
    !!modalData?.channelId || (modalData?.channelIds?.length ?? 0) > 0

  const isTrialCancellation =
    modalData?.isTrialCancellation ??
    modalData?.isOnTrial ??
    (modalData?.subscriptionType != null
      ? modalData.subscriptionType === 'trial'
      : undefined)
  const shouldRedirectCancelFlowToBilling =
    !userCanManageBilling ||
    !isOneBufferOrg ||
    (isFreeUser(user) && isTrialCancellation !== true)

  const renderPlanSelectorDialog = (): JSX.Element => (
    <PlanSelectorDialog
      onClose={dismissModal}
      cta={modalData?.cta}
      ctaButton={modalData?.ctaButton}
      upgradePathName={modalData?.upgradePathName}
    />
  )

  const renderCancelFlow = (): JSX.Element | null => {
    if (shouldRedirectCancelFlowToBilling) {
      redirectToUrl(
        getBillingPageUrl(
          !isOneBufferOrg
            ? 'cancel-flow-legacy-redirect'
            : userCanManageBilling
              ? 'cancel-flow-already-free'
              : 'cancel-flow-no-billing-permission',
        ),
      )
      return null
    }

    return (
      <CancelFlowModal
        dismissModal={dismissModal}
        isTrialCancellation={isTrialCancellation}
      />
    )
  }

  switch (modalKey as MODALS) {
    case MODALS.downloadApp:
      return <DownloadAppModal closeAction={dismissModal} />

    case MODALS.channelConnectionPrompt:
      return (
        <StickyModal closeAction={dismissModal}>
          <ChannelConnectionPrompt closeAction={dismissModal} />
        </StickyModal>
      )
    case MODALS.planSelector:
      if (userCanManageBilling && isOneBufferOrg) {
        return renderPlanSelectorDialog()
      }
      redirectToUrl(
        getBillingPageUrl(
          isOneBufferOrg
            ? 'plan-selector-no-billing-permission'
            : 'plan-selector-legacy-redirect',
        ),
      )
      return null

    case MODALS.instagramFacebookReAuth:
      return <InstagramFacebookReAuth />

    case MODALS.instagramSelectAccountType:
      return <InstagramSelectAccountType />
    case MODALS.instagramSwitchToProfessionalAccountWarning:
      return <InstagramSwitchToProfessionalAccountWarning />
    case MODALS.facebookSelectAccountType:
      return <FacebookSelectAccountTypeDialog />
    case MODALS.switchFromIGLoginToFBLogin:
      return <SwitchFromIGLoginToFBLoginDialog />
    case MODALS.success:
      if (!isOnboardingPage()) {
        return <Confirmation />
      }
      return null

    case MODALS.startTrial:
    case MODALS.startTrialNoClick:
      if (userCanManageBilling) {
        return <StartTrialModal />
      }
      return null

    case MODALS.featureTrial:
      return <FeatureTrialModal />

    case MODALS.trialExpired:
      return <TrialExpired open={true} onOpenChange={() => dismissModal()} />

    case MODALS.featurePaywall:
      return (
        <Dialog
          open
          onOpenChange={(isOpen): void => {
            if (!isOpen) {
              dismissModal()
            }
          }}
        >
          <Dialog.Content className={styles.featurePaywallDialog}>
            <VisuallyHidden as="div">
              <Dialog.Title>
                {modalData?.featurePaywallProperties?.headline ?? 'Feature'}
              </Dialog.Title>
            </VisuallyHidden>
            <Dialog.CloseButton />
            <FeaturePaywall />
          </Dialog.Content>
        </Dialog>
      )

    case MODALS.subscriptionUpdate:
      if (userCanManageBilling && isOneBufferOrg) {
        return renderPlanSelectorDialog()
      }
      redirectToUrl(
        getBillingPageUrl(
          isOneBufferOrg
            ? 'plan-selector-no-billing-permission'
            : 'plan-selector-legacy-redirect',
        ),
      )
      return null

    case MODALS.channelConnectionSuccessModal:
      if (
        shouldShowChannelConnectionOnboarding &&
        !shouldShowConnectChannelUpgradeModal &&
        shouldShowPostingGoalConfiguration
      ) {
        cleanupUrlParams()
        const PostingGoalComponent = useNewChannelSuccessModal
          ? ChannelConnectionSuccess
          : PostingGoalConfiguration
        return (
          <PostingGoalComponent
            channelIds={
              modalData?.channelIds ?? [modalData?.channelId as string]
            }
            dismissModal={dismissModal}
          />
        )
      }
      if (shouldShowConnectChannelUpgradeModal) {
        return (
          <ConnectChannelUpgrade onOpenChange={(): void => dismissModal()} />
        )
      }
      return null

    case MODALS.channelRefreshSuccessModal:
      if (
        shouldShowChannelConnectionOnboarding &&
        !shouldShowConnectChannelUpgradeModal
      ) {
        return <ChannelRefreshSuccessModal />
      }
      return null

    // NOTE: We don't actually call this modal directly via apps any more.
    // We use the above MODALS.channelConnectionSuccessModal event to decide if this modal is shown
    // Keeping this logic here for now to make it easier to test.
    // REFACTOR: after consuming appshell directly in products and testing can be done in app we should then delete this.
    case MODALS.connectChannelUpgrade:
      if (shouldShowConnectChannelUpgradeModal) {
        return (
          <ConnectChannelUpgrade onOpenChange={(): void => dismissModal()} />
        )
      }
      return null

    case MODALS.checklistComplete:
      if (!hasSeenChecklistCompleteModal(user)) {
        return <ChecklistComplete closeAction={dismissModal} />
      }
      return null

    case MODALS.churnSurvey:
    case MODALS.cancelFlow:
      return renderCancelFlow()

    case MODALS.confirmCancellation:
      return isTrialCancellation ? (
        renderCancelFlow()
      ) : (
        <ConfirmCancellationModal />
      )

    case MODALS.trialSignupWelcomeModal:
      if (shouldShowTrialSignupWelcomeModal(user)) {
        return <TrialSignupWelcomeModal dismissModal={dismissModal} />
      }
      return null

    case MODALS.setupNotifications:
      return (
        <RemindersWizard
          source={modalData?.source}
          onContinue={modalData?.onContinue}
          onDismiss={modalData?.onDismiss}
          closeAction={dismissModal}
        />
      )

    case MODALS.teamMemberDowngrade:
      return <TeamMemberDowngradeModal closeAction={dismissModal} />

    case MODALS.postingPlanConfiguration:
      if (modalData?.channelId) {
        return <PostingPlanConfiguration channelId={modalData?.channelId} />
      }
      return null

    case MODALS.postingGoalConfiguration: {
      const channelIds = modalData?.channelIds?.length
        ? modalData.channelIds
        : modalData?.channelId
          ? [modalData.channelId]
          : []
      if (channelIds.length > 0) {
        return (
          <PostingGoalConfiguration
            channelIds={channelIds}
            dismissModal={dismissModal}
            remindLater={
              modalData?.source !== 'channel-page' &&
              modalData?.source !== 'home-page'
            }
            onPostingGoalUpdated={modalData?.onPostingGoalUpdated}
            returnFocusToId={modalData?.returnFocusToId}
          />
        )
      }
      return null
    }

    case MODALS.channelGroupsPrompt:
      return <ChannelGroupsPrompt />

    case MODALS.subscriptionUpdateModern:
      return <SubscriptionUpdateModal />

    case MODALS.analyzeUpgradePath:
      return <AnalyzeUpgradePath dismissModal={dismissModal} />

    case MODALS.annualUpgrade:
      // Entry points (banner, email deeplink, cancel flow) own the tenure
      // gating, so only the hard eligibility checks apply here.
      if (!isEligibleForAnnualUpgrade(user, 0)) {
        return null
      }
      return <AnnualUpgradeModal onClose={dismissModal} />

    case MODALS.linkedInCapabilities:
      return <LinkedInCapabilities />

    default:
      return null
  }
}

const ModalContentMemo = React.memo(ModalContent)
export { ModalContentMemo as ModalContent }
